// =====================================================================
// tpCustomer — Customer churn
//
// Renewal-led retention dashboard for a small, high-touch B2B SaaS
// (Trusted Path). Two tabs share a single period selector + data layer:
//
//   Dashboard  — current-state tiles + rolling-12-month trend charts.
//                Honest about small sample sizes (n= beside every %,
//                muted banner if active cohort < 20).
//   Details    — sortable / filterable per-account record with leading
//                product signals, at-risk auto-flag suggestion, and
//                inline edit of the qualitative fields. Drawer shows
//                full record incl. monthly usage history.
//
// All metric computation lives in churn-metrics.js (window.tpChurn).
// This file is the UI layer + data adaptation.
// =====================================================================

// ── PeriodSelector ───────────────────────────────────────────────────
// Single selector lives in the page header so both tabs read the same
// window. Defaults to rolling-12 (the brief's default).
function ChurnPeriodSelector({ periodKey, onChange }) {
  return (
    <select
      aria-label="Reporting period"
      className="period-selector"
      value={periodKey}
      onChange={e => onChange(e.target.value)}
      style={{ minWidth: 180 }}
    >
      <option value="rolling-6">Rolling 6 months</option>
      <option value="rolling-12">Rolling 12 months</option>
      <option value="ytd">This calendar year</option>
    </select>
  );
}

// ── Small-sample banner ──────────────────────────────────────────────
// Renders once at the top of the dashboard when the active cohort is
// below 20 accounts — small-N reading rule from the brief.
function SmallSampleBanner({ n }) {
  if (n >= 20) return null;
  return (
    <div className="card" style={{ background: "var(--bg-soft)", padding: "10px 14px", marginBottom: 16, color: "var(--text-muted)", fontSize: 13 }}>
      <strong style={{ color: "var(--text)" }}>Small sample (n={n}).</strong>{" "}
      Read rates as directional only. Counts and named accounts are the primary signal.
    </div>
  );
}

// ── Stat tile (extends ui.jsx Stat with n= and clickability) ─────────
function ChurnTile({ label, value, sublabel, n, tone, onClick, hero }) {
  const cls = ["stat"];
  if (onClick) cls.push("stat-clickable");
  if (hero) cls.push("stat-hero");
  if (tone) cls.push("stat-tone-" + tone);
  const role = onClick ? "button" : undefined;
  const tabIndex = onClick ? 0 : undefined;
  const onKey = onClick ? (e => { if (e.key === "Enter" || e.key === " ") { e.preventDefault(); onClick(); } }) : undefined;
  return (
    <div
      className={cls.join(" ")}
      onClick={onClick}
      onKeyDown={onKey}
      role={role}
      tabIndex={tabIndex}
      style={{ cursor: onClick ? "pointer" : "default", position: "relative" }}
    >
      <div className="stat-label">{label}</div>
      <div className="stat-value numeric">{value}</div>
      <div style={{ display: "flex", gap: 8, alignItems: "baseline", marginTop: 4 }}>
        {sublabel && <span className="stat-delta" style={{ color: "var(--text-muted)", fontSize: 11 }}>{sublabel}</span>}
        {n != null && (
          <span className="stat-delta" style={{ color: "var(--text-muted)", fontSize: 11 }}>
            n={n}
          </span>
        )}
      </div>
    </div>
  );
}

// ── Charts (inline SVG; no external library) ─────────────────────────

// Monthly bar chart — bar height = ARR, fill colour = health. Used for
// the renewal calendar. Click a bar to filter the Details tab to that
// month's renewals.
function RenewalCalendarChart({ series, onBarClick }) {
  const tpC = window.tpChurn;
  const W = 720, H = 220, padL = 48, padR = 12, padT = 14, padB = 36;
  const innerW = W - padL - padR;
  const innerH = H - padT - padB;
  const maxArr = Math.max(1, ...series.map(b => b.arrPence));
  const barW = innerW / Math.max(series.length, 1) * 0.7;
  const stepX = innerW / Math.max(series.length, 1);

  const yTicks = 4;
  const ticks = [];
  for (let i = 0; i <= yTicks; i++) {
    const value = (maxArr / yTicks) * i;
    const y = padT + innerH - (innerH * (value / maxArr));
    ticks.push({ value, y });
  }

  function toneFor(b) {
    if (b.health === "red")    return "var(--red)";
    if (b.health === "amber")  return "var(--amber)";
    if (b.health === "green")  return "var(--green)";
    return "var(--border)";
  }

  return (
    <svg viewBox={`0 0 ${W} ${H}`} width="100%" role="img" aria-label="Upcoming renewals by month">
      {ticks.map((t, i) => (
        <g key={i}>
          <line x1={padL} x2={padL + innerW} y1={t.y} y2={t.y} stroke="var(--border)" strokeDasharray="2 3" />
          <text x={padL - 6} y={t.y + 3} fontSize="10" fill="var(--text-muted)" textAnchor="end">
            {tpC.formatPoundsCompact(t.value)}
          </text>
        </g>
      ))}
      {series.map((b, i) => {
        const x = padL + i * stepX + (stepX - barW) / 2;
        const h = b.arrPence > 0 ? (innerH * (b.arrPence / maxArr)) : 0;
        const y = padT + innerH - h;
        const label = formatMonthLabel(b.month);
        const accountsText = b.accounts.length
          ? `${b.accounts.length} account${b.accounts.length === 1 ? "" : "s"}: ${b.accounts.map(a => a.name || a.accountId).join(", ")}`
          : "No renewals";
        return (
          <g key={b.month}>
            <rect
              x={x} y={y} width={barW} height={h}
              fill={toneFor(b)} opacity={b.arrPence > 0 ? 0.9 : 0.3}
              style={{ cursor: b.arrPence > 0 ? "pointer" : "default" }}
              onClick={() => b.arrPence > 0 && onBarClick && onBarClick(b)}
              tabIndex={b.arrPence > 0 ? 0 : -1}
              role={b.arrPence > 0 ? "button" : undefined}
            >
              <title>{label}: {tpC.formatPounds(b.arrPence)} · {accountsText}</title>
            </rect>
            <text x={x + barW / 2} y={H - padB + 14} fontSize="10" fill="var(--text-muted)" textAnchor="middle">
              {label}
            </text>
          </g>
        );
      })}
    </svg>
  );
}

// ARR-bridge bar chart. Stack: new (green) on top of upgrade (blue), then
// downgrade + churn (red, below baseline). Caller passes signed values.
function ArrBridgeChart({ series }) {
  const tpC = window.tpChurn;
  const W = 720, H = 220, padL = 48, padR = 12, padT = 14, padB = 36;
  const innerW = W - padL - padR;
  const innerH = H - padT - padB;
  const stepX = innerW / Math.max(series.length, 1);
  const barW = stepX * 0.6;
  // Determine positive/negative max so the zero axis sits correctly.
  let posMax = 0, negMax = 0;
  for (const b of series) {
    const pos = (b.newArr || 0) + (b.upgrade || 0);
    const neg = Math.abs((b.downgrade || 0) + (b.churn || 0));
    if (pos > posMax) posMax = pos;
    if (neg > negMax) negMax = neg;
  }
  const totalMax = Math.max(1, posMax + negMax);
  const zeroY = padT + (innerH * (posMax / totalMax));

  return (
    <svg viewBox={`0 0 ${W} ${H}`} width="100%" role="img" aria-label="ARR bridge over trailing 12 months">
      <line x1={padL} x2={padL + innerW} y1={zeroY} y2={zeroY} stroke="var(--border)" />
      <text x={padL - 6} y={zeroY + 3} fontSize="10" fill="var(--text-muted)" textAnchor="end">£0</text>
      <text x={padL - 6} y={padT + 8} fontSize="10" fill="var(--text-muted)" textAnchor="end">
        {tpC.formatPoundsCompact(posMax)}
      </text>
      <text x={padL - 6} y={padT + innerH - 2} fontSize="10" fill="var(--text-muted)" textAnchor="end">
        {tpC.formatPoundsCompact(-negMax)}
      </text>
      {series.map((b, i) => {
        const x = padL + i * stepX + (stepX - barW) / 2;
        const pos = (b.newArr || 0) + (b.upgrade || 0);
        const neg = Math.abs((b.downgrade || 0) + (b.churn || 0));
        const posH = (pos / totalMax) * innerH;
        const negH = (neg / totalMax) * innerH;
        const upgradeH = (Math.abs(b.upgrade || 0) / totalMax) * innerH;
        const newH = (Math.abs(b.newArr || 0) / totalMax) * innerH;
        const churnH = (Math.abs(b.churn || 0) / totalMax) * innerH;
        const downH = (Math.abs(b.downgrade || 0) / totalMax) * innerH;
        return (
          <g key={b.month}>
            {/* Positive stack — upgrade then new on top */}
            <rect x={x} y={zeroY - upgradeH} width={barW} height={upgradeH} fill="var(--blue, #3b82f6)" opacity="0.85">
              <title>{formatMonthLabel(b.month)} · Upgrade {tpC.formatPounds(b.upgrade || 0)}</title>
            </rect>
            <rect x={x} y={zeroY - upgradeH - newH} width={barW} height={newH} fill="var(--green)" opacity="0.85">
              <title>{formatMonthLabel(b.month)} · New {tpC.formatPounds(b.newArr || 0)}</title>
            </rect>
            {/* Negative stack — downgrade then churn underneath */}
            <rect x={x} y={zeroY} width={barW} height={downH} fill="var(--amber)" opacity="0.85">
              <title>{formatMonthLabel(b.month)} · Downgrade {tpC.formatPounds(b.downgrade || 0)}</title>
            </rect>
            <rect x={x} y={zeroY + downH} width={barW} height={churnH} fill="var(--red)" opacity="0.85">
              <title>{formatMonthLabel(b.month)} · Churn {tpC.formatPounds(b.churn || 0)}</title>
            </rect>
            <text x={x + barW / 2} y={H - padB + 14} fontSize="10" fill="var(--text-muted)" textAnchor="middle">
              {formatMonthLabel(b.month)}
            </text>
          </g>
        );
      })}
    </svg>
  );
}

// Two-line trend chart for GRR and NRR. Skips months with n=0.
function RetentionTrendChart({ series }) {
  const W = 720, H = 200, padL = 48, padR = 12, padT = 18, padB = 32;
  const innerW = W - padL - padR;
  const innerH = H - padT - padB;
  const stepX = innerW / Math.max(series.length - 1, 1);
  // Anchor y on a reasonable retention range (50-130%) so the chart is readable.
  const yMin = 0.5, yMax = 1.3;
  function yFor(v) {
    if (v == null) return null;
    const clamped = Math.max(yMin, Math.min(yMax, v));
    return padT + innerH - (innerH * ((clamped - yMin) / (yMax - yMin)));
  }
  function pathFor(key) {
    let d = "";
    series.forEach((p, i) => {
      const x = padL + i * stepX;
      const y = yFor(p[key]);
      if (y == null) { return; }
      d += (d ? " L " : "M ") + x.toFixed(1) + " " + y.toFixed(1);
    });
    return d;
  }
  const grrPath = pathFor("grr");
  const nrrPath = pathFor("nrr");
  const yTicks = [0.6, 0.8, 1.0, 1.2];
  return (
    <svg viewBox={`0 0 ${W} ${H}`} width="100%" role="img" aria-label="GRR and NRR trend by renewal-cohort month">
      {yTicks.map(v => (
        <g key={v}>
          <line x1={padL} x2={padL + innerW} y1={yFor(v)} y2={yFor(v)} stroke="var(--border)" strokeDasharray="2 3" />
          <text x={padL - 6} y={yFor(v) + 3} fontSize="10" fill="var(--text-muted)" textAnchor="end">{Math.round(v * 100)}%</text>
        </g>
      ))}
      {/* 100% line */}
      <line x1={padL} x2={padL + innerW} y1={yFor(1)} y2={yFor(1)} stroke="var(--text-muted)" strokeDasharray="3 3" opacity="0.6" />
      <path d={grrPath} stroke="var(--blue, #3b82f6)" fill="none" strokeWidth="2" />
      <path d={nrrPath} stroke="var(--green)" fill="none" strokeWidth="2" />
      {series.map((p, i) => {
        const x = padL + i * stepX;
        return (
          <g key={p.month}>
            {p.grr != null && (
              <circle cx={x} cy={yFor(p.grr)} r="3" fill="var(--blue, #3b82f6)">
                <title>{formatMonthLabel(p.month)} GRR {Math.round(p.grr * 100)}% (n={p.n})</title>
              </circle>
            )}
            {p.nrr != null && (
              <circle cx={x} cy={yFor(p.nrr)} r="3" fill="var(--green)">
                <title>{formatMonthLabel(p.month)} NRR {Math.round(p.nrr * 100)}% (n={p.n})</title>
              </circle>
            )}
            <text x={x} y={H - padB + 14} fontSize="10" fill="var(--text-muted)" textAnchor="middle">
              {formatMonthLabel(p.month)}
            </text>
          </g>
        );
      })}
      <g transform={`translate(${padL + 6}, ${padT - 4})`}>
        <rect x="0" y="-8" width="10" height="2" fill="var(--blue, #3b82f6)" />
        <text x="14" y="-3" fontSize="11" fill="var(--text)">GRR</text>
        <rect x="50" y="-8" width="10" height="2" fill="var(--green)" />
        <text x="64" y="-3" fontSize="11" fill="var(--text)">NRR</text>
      </g>
    </svg>
  );
}

function LogoCountTrendChart({ series }) {
  const W = 720, H = 160, padL = 36, padR = 12, padT = 14, padB = 32;
  const innerW = W - padL - padR;
  const innerH = H - padT - padB;
  const max = Math.max(1, ...series.map(s => s.active));
  const stepX = innerW / Math.max(series.length - 1, 1);
  function yFor(v) { return padT + innerH - (innerH * (v / max)); }
  let d = "";
  series.forEach((p, i) => {
    const x = padL + i * stepX;
    d += (i === 0 ? "M " : " L ") + x.toFixed(1) + " " + yFor(p.active).toFixed(1);
  });
  return (
    <svg viewBox={`0 0 ${W} ${H}`} width="100%" role="img" aria-label="Active logo count by month">
      <line x1={padL} x2={padL + innerW} y1={padT + innerH} y2={padT + innerH} stroke="var(--border)" />
      <text x={padL - 4} y={padT + 8} fontSize="10" fill="var(--text-muted)" textAnchor="end">{max}</text>
      <text x={padL - 4} y={padT + innerH + 3} fontSize="10" fill="var(--text-muted)" textAnchor="end">0</text>
      <path d={d} stroke="var(--accent)" fill="none" strokeWidth="2" />
      {series.map((p, i) => {
        const x = padL + i * stepX;
        return (
          <g key={p.month}>
            <circle cx={x} cy={yFor(p.active)} r="3" fill="var(--accent)">
              <title>{formatMonthLabel(p.month)}: {p.active} active logos</title>
            </circle>
            <text x={x} y={H - padB + 14} fontSize="10" fill="var(--text-muted)" textAnchor="middle">
              {formatMonthLabel(p.month)}
            </text>
          </g>
        );
      })}
    </svg>
  );
}

// Sparkline for a single account's login history (12 months). Compact —
// designed to sit inline in a table cell.
function MiniSparkline({ values, width, height, tone }) {
  const W = width || 80, H = height || 22;
  const max = Math.max(1, ...values);
  const stepX = W / Math.max(values.length - 1, 1);
  let d = "";
  values.forEach((v, i) => {
    const x = i * stepX;
    const y = H - (H * (v / max));
    d += (i === 0 ? "M " : " L ") + x.toFixed(1) + " " + y.toFixed(1);
  });
  return (
    <svg viewBox={`0 0 ${W} ${H}`} width={W} height={H} style={{ verticalAlign: "middle" }} aria-hidden="true">
      <path d={d} stroke={tone || "var(--accent)"} fill="none" strokeWidth="1.5" />
    </svg>
  );
}

// Month label like "May 26".
function formatMonthLabel(monthIso) {
  if (!monthIso) return "";
  const d = new Date(monthIso);
  return d.toLocaleDateString("en-GB", { month: "short", year: "2-digit" });
}

// ── Data adaptation helpers ───────────────────────────────────────────
// Build the dashboard's account list from churn_qualitative joined with
// the latest usage_snapshot. Adds at-risk auto-flag verdict per account.
function enrichAccount(qual, snapshots, accountsById) {
  const acct = accountsById[qual.accountId] || {};
  const tpC = window.tpChurn;
  const usage = tpC.latestAndPrior(snapshots, qual.accountId);
  const ruleEval = tpC.evaluateAtRiskRules(usage.latest, usage.prior);
  // "Flag suggested" badge only shows when the system suggests at_risk
  // BUT the user has not confirmed. Once confirmed, status becomes
  // 'at_risk' and the badge is unnecessary.
  const flagSuggested =
    ruleEval.suggested && qual.status === "active" && !qual.atRiskConfirmed;
  return {
    ...qual,
    accountName: qual.accountName || acct.name || qual.accountId,
    industry: acct.industry,
    region: qual.accountRegion || acct.region,
    latest: usage.latest,
    prior: usage.prior,
    snapshots: usage.all,
    ruleEval,
    flagSuggested,
    daysToRenewal: tpC.daysToRenewal(qual),
    tenureMonths: tpC.tenureMonths(qual),
  };
}

// ── ChurnPage ────────────────────────────────────────────────────────
function ChurnPage({ navigate }) {
  const [tab, setTab] = useState("dashboard");
  const [periodKey, setPeriodKey] = useState("rolling-12");
  // Cross-tab filter set by clicking a tile / bar on the Dashboard.
  // Keys: "renewal-pool" (renewals in next 90 days), "churned" (churned
  // in period), "at-risk" (status=at_risk), or { month: "YYYY-MM-01" }.
  const [quickFilter, setQuickFilter] = useState(null);

  const tpC = window.tpChurn;
  const period = useMemo(() => tpC.presetPeriod(periodKey), [periodKey]);

  // ── Adapt window.TPCRM_DATA → metrics inputs ──────────────────────
  const accountsById = useMemo(() => {
    const map = {};
    for (const a of (D.ACCOUNTS || [])) map[a.id] = a;
    return map;
  }, [D.ACCOUNTS]);

  const enriched = useMemo(() =>
    (D.CHURN_QUALITATIVE || []).map(q => enrichAccount(q, D.USAGE_SNAPSHOTS || [], accountsById)),
    [D.CHURN_QUALITATIVE, D.USAGE_SNAPSHOTS, accountsById]
  );

  const renewalEvents = D.RENEWAL_EVENTS || [];

  const tabs = [
    { key: "dashboard", label: "Dashboard" },
    { key: "details", label: "Details", count: enriched.length },
  ];

  function onTileClick(filterKey) {
    setQuickFilter(filterKey);
    setTab("details");
  }

  return (
    <>
      <PageHead
        title="Churn"
        sub="Renewal-led retention — small cohort, read counts before rates"
        actions={
          <>
            <ChurnPeriodSelector periodKey={periodKey} onChange={setPeriodKey} />
          </>
        }
      />
      <Tabs tabs={tabs} current={tab} onChange={setTab} />
      {tab === "dashboard"
        ? <ChurnDashboardTab
            accounts={enriched}
            renewalEvents={renewalEvents}
            period={period}
            onTileClick={onTileClick}
          />
        : <ChurnDetailsTab
            accounts={enriched}
            renewalEvents={renewalEvents}
            period={period}
            quickFilter={quickFilter}
            onClearQuickFilter={() => setQuickFilter(null)}
          />
      }
    </>
  );
}

// ── Dashboard tab ────────────────────────────────────────────────────
function ChurnDashboardTab({ accounts, renewalEvents, period, onTileClick }) {
  const tpC = window.tpChurn;

  const active     = tpC.activeLogos(accounts);
  const live       = tpC.liveArr(accounts);
  const upcoming   = tpC.arrUpForRenewal(accounts, new Date(), 90);
  const atRisk     = tpC.atRiskArr(accounts, new Date(), 90);
  const churned    = tpC.logosChurnedInPeriod(renewalEvents, period);
  const arrChurned = tpC.arrChurnedInPeriod(renewalEvents, period);
  const grr        = tpC.grrInPeriod(renewalEvents, period);
  const nrr        = tpC.nrrInPeriod(renewalEvents, period);

  const calendar = useMemo(() => tpC.renewalCalendar(accounts, new Date(), 12), [accounts]);
  const bridge   = useMemo(() => tpC.arrBridge(renewalEvents, period), [renewalEvents, period]);
  const trend    = useMemo(() => tpC.retentionTrend(renewalEvents, period), [renewalEvents, period]);
  const logoLine = useMemo(() => tpC.logoCountTrend(accounts, renewalEvents, period), [accounts, renewalEvents, period]);

  return (
    <>
      <SmallSampleBanner n={active.n} />

      {/* Row 1 — counts (primary signal, no percentages) */}
      <div className="grid grid-4" style={{ marginBottom: 12 }}>
        <ChurnTile label="Active logos" value={active.value} sublabel="customers in book" />
        <ChurnTile label="Live ARR" value={tpC.formatPounds(live.value)} n={live.n} sublabel="across active customers" />
        <ChurnTile
          label="ARR up for renewal · next 90d"
          value={tpC.formatPounds(upcoming.value)}
          n={upcoming.n}
          sublabel="click to see accounts"
          onClick={upcoming.n ? () => onTileClick("renewal-pool") : null}
        />
        <ChurnTile
          label="At-risk ARR · next 90d"
          value={tpC.formatPounds(atRisk.value)}
          n={atRisk.n}
          tone="red"
          hero
          sublabel={atRisk.pool ? `of ${atRisk.pool} renewing` : null}
          onClick={atRisk.n ? () => onTileClick("at-risk") : null}
        />
      </div>

      {/* Row 2 — period-bound churn outcomes */}
      <div className="grid grid-4" style={{ marginBottom: 16 }}>
        <ChurnTile
          label={`Logos churned · ${period.label.toLowerCase()}`}
          value={churned.value}
          sublabel="non-renewals"
          onClick={churned.value ? () => onTileClick("churned") : null}
        />
        <ChurnTile
          label="ARR churned (incl. downgrades)"
          value={tpC.formatPounds(arrChurned.value)}
          n={arrChurned.n}
          tone={arrChurned.value > 0 ? "amber" : null}
        />
        <ChurnTile
          label="GRR (renewed cohort)"
          value={grr.value != null ? tpC.formatPct(grr.value) : "—"}
          n={grr.n}
          sublabel={grr.n < 5 ? "cohort small — read directionally" : "of start ARR retained"}
        />
        <ChurnTile
          label="NRR (renewed cohort)"
          value={nrr.value != null ? tpC.formatPct(nrr.value) : "—"}
          n={nrr.n}
          sublabel={nrr.n < 5 ? "cohort small — read directionally" : null}
        />
      </div>

      {/* Hero trend — renewal calendar + active logo count side-by-side */}
      <div className="grid grid-2" style={{ marginBottom: 16, gap: 16 }}>
        <div className="card">
          <div className="card-head">
            <div>
              <div className="card-title">Upcoming renewals · next 12 months</div>
              <div className="card-sub">Bar height = ARR · colour = health (red = at-risk in bucket)</div>
            </div>
            <div style={{ display: "flex", gap: 12, fontSize: 12, color: "var(--text-muted)" }}>
              <span><span style={{ display: "inline-block", width: 10, height: 10, background: "var(--green)", marginRight: 4, verticalAlign: "middle" }} />healthy</span>
              <span><span style={{ display: "inline-block", width: 10, height: 10, background: "var(--red)", marginRight: 4, verticalAlign: "middle" }} />at-risk in bucket</span>
            </div>
          </div>
          <div className="card-body">
            <RenewalCalendarChart series={calendar} onBarClick={(b) => onTileClick({ month: b.month })} />
          </div>
        </div>
        <div className="card">
          <div className="card-head">
            <div>
              <div className="card-title">Active logo count</div>
              <div className="card-sub">Monthly count of non-churned customers · for context, not a percentage</div>
            </div>
          </div>
          <div className="card-body">
            <LogoCountTrendChart series={logoLine} />
          </div>
        </div>
      </div>

      {/* Bridge + trend */}
      <div className="grid grid-2" style={{ marginBottom: 16, gap: 16 }}>
        <div className="card">
          <div className="card-head">
            <div>
              <div className="card-title">ARR bridge · trailing 12 months</div>
              <div className="card-sub">Anchored on renewal-event month · green new · blue upgrade · amber downgrade · red churn</div>
            </div>
          </div>
          <div className="card-body">
            <ArrBridgeChart series={bridge} />
          </div>
        </div>
        <div className="card">
          <div className="card-head">
            <div>
              <div className="card-title">GRR / NRR by renewal cohort</div>
              <div className="card-sub">Months with no renewals are skipped — small cohort is honest</div>
            </div>
          </div>
          <div className="card-body">
            <RetentionTrendChart series={trend} />
          </div>
        </div>
      </div>
    </>
  );
}

// ── Details tab ──────────────────────────────────────────────────────
function ChurnDetailsTab({ accounts, renewalEvents, period, quickFilter, onClearQuickFilter }) {
  const tpC = window.tpChurn;
  const [statusFilter, setStatusFilter] = useState("all");
  const [reasonFilter, setReasonFilter] = useState("all");
  const [ownerFilter, setOwnerFilter] = useState("all");
  const [windowFilter, setWindowFilter] = useState("all");
  const [search, setSearch] = useState("");
  const [sortKey, setSortKey] = useState("renewalDate");
  const [sortDir, setSortDir] = useState("asc");
  const [openId, setOpenId] = useState(null);
  const [editId, setEditId] = useState(null);

  // Apply the dashboard's quick-filter — sets multiple controls in one shot.
  useEffect(() => {
    if (!quickFilter) return;
    if (quickFilter === "renewal-pool") { setStatusFilter("all"); setWindowFilter("90d"); }
    else if (quickFilter === "at-risk")  { setStatusFilter("at_risk"); setWindowFilter("90d"); }
    else if (quickFilter === "churned")  { setStatusFilter("churned"); setWindowFilter("all"); }
    else if (quickFilter && quickFilter.month) {
      setStatusFilter("all"); setWindowFilter("month:" + quickFilter.month);
    }
  }, [quickFilter]);

  const today = new Date();
  const horizons = { "30d": 30, "60d": 60, "90d": 90 };

  const rows = useMemo(() => {
    let r = accounts.slice();
    if (statusFilter !== "all") r = r.filter(a => a.status === statusFilter);
    if (reasonFilter !== "all") r = r.filter(a => (a.reasonCategory || "") === reasonFilter);
    if (ownerFilter !== "all")  r = r.filter(a => a.renewalOwnerId === ownerFilter);
    if (search.trim()) {
      const s = search.trim().toLowerCase();
      r = r.filter(a => (a.accountName || "").toLowerCase().includes(s));
    }
    if (windowFilter !== "all") {
      if (windowFilter.startsWith("month:")) {
        const m = windowFilter.slice(6);
        r = r.filter(a => a.renewalDate && a.renewalDate.slice(0, 7) === m.slice(0, 7));
      } else if (horizons[windowFilter]) {
        const days = horizons[windowFilter];
        r = r.filter(a => {
          if (!a.renewalDate) return false;
          const d = tpC._internals.dayDiff(a.renewalDate, today);
          return d >= 0 && d <= days;
        });
      }
    }
    r.sort((a, b) => {
      const av = a[sortKey], bv = b[sortKey];
      let cmp;
      if (av == null && bv == null) cmp = 0;
      else if (av == null) cmp = 1;
      else if (bv == null) cmp = -1;
      else if (typeof av === "number" && typeof bv === "number") cmp = av - bv;
      else cmp = String(av).localeCompare(String(bv));
      return sortDir === "asc" ? cmp : -cmp;
    });
    return r;
  }, [accounts, statusFilter, reasonFilter, ownerFilter, windowFilter, search, sortKey, sortDir]);

  function toggleSort(key) {
    if (sortKey === key) setSortDir(sortDir === "asc" ? "desc" : "asc");
    else { setSortKey(key); setSortDir("asc"); }
  }

  function exportCsv() {
    const headers = [
      "Account", "Status", "Tier", "ARR (GBP)", "Contract start", "Contract end",
      "Renewal date", "Days to renewal", "Tenure (months)", "Renewal owner",
      "Reason", "Reason narrative", "Champion left",
      "Save attempted", "Save outcome", "Recoverable", "Win-back note",
      "Activation reached", "Time-to-value (days)",
      "Days since last login", "Logins (30d)", "Active seats", "Licensed seats",
      "Assessments run", "Compliance mappings", "Requirements docs",
      "Open escalations", "Health score",
    ];
    const csvRows = [headers];
    for (const a of rows) {
      const l = a.latest || {};
      csvRows.push([
        a.accountName, a.status, a.tier,
        a.arrPence != null ? (a.arrPence / 100).toFixed(2) : "",
        a.contractStart || "", a.contractEnd || "", a.renewalDate || "",
        a.daysToRenewal != null ? a.daysToRenewal : "",
        a.tenureMonths != null ? a.tenureMonths : "",
        getUser(a.renewalOwnerId)?.name || "",
        a.reasonCategory || "", a.reasonNarrative || "",
        a.championLeft ? "Yes" : "No",
        a.saveAttempted ? "Yes" : "No", a.saveOutcome || "",
        a.recoverable ? "Yes" : "No", a.winBackNote || "",
        l.activationReached ? "Yes" : "No",
        l.timeToValueDays != null ? l.timeToValueDays : "",
        l.daysSinceLastLogin != null ? l.daysSinceLastLogin : "",
        l.logins30d != null ? l.logins30d : "",
        l.activeSeats != null ? l.activeSeats : "",
        l.licensedSeats != null ? l.licensedSeats : "",
        l.assessmentsRun != null ? l.assessmentsRun : "",
        l.complianceMappings != null ? l.complianceMappings : "",
        l.requirementsDocs != null ? l.requirementsDocs : "",
        l.openEscalations != null ? l.openEscalations : "",
        l.healthScore != null ? l.healthScore : "",
      ]);
    }
    const csv = csvRows.map(row =>
      row.map(v => {
        const s = v == null ? "" : String(v);
        return /[",\n]/.test(s) ? `"${s.replace(/"/g, '""')}"` : s;
      }).join(",")
    ).join("\r\n");
    const blob = new Blob([csv], { type: "text/csv;charset=utf-8;" });
    const url = URL.createObjectURL(blob);
    const a = document.createElement("a");
    a.href = url;
    a.download = `churn-details-${new Date().toISOString().slice(0, 10)}.csv`;
    document.body.appendChild(a); a.click(); a.remove();
    URL.revokeObjectURL(url);
  }

  const reps = assignableReps(["CSM", "AE"]);
  const open = rows.find(r => r.accountId === openId) || null;

  function sortHeader(label, key, right) {
    const active = sortKey === key;
    return (
      <th
        className={right ? "right" : ""}
        onClick={() => toggleSort(key)}
        style={{ cursor: "pointer", userSelect: "none" }}
        aria-sort={active ? (sortDir === "asc" ? "ascending" : "descending") : "none"}
      >
        {label}{active ? (sortDir === "asc" ? " ▲" : " ▼") : ""}
      </th>
    );
  }

  return (
    <>
      {quickFilter && (
        <div className="card" style={{ background: "var(--bg-soft)", padding: "8px 12px", marginBottom: 12, fontSize: 12, display: "flex", justifyContent: "space-between", alignItems: "center" }}>
          <span>Filtered from dashboard — <strong>{describeQuickFilter(quickFilter)}</strong></span>
          <button className="btn btn-sm" onClick={onClearQuickFilter}>Clear</button>
        </div>
      )}
      <div className="card">
        <FilterToolbar count={rows.length} total={accounts.length}>
          <input
            type="text" placeholder="Search…" value={search}
            onChange={e => setSearch(e.target.value)} aria-label="Search accounts"
          />
          <select value={statusFilter} onChange={e => setStatusFilter(e.target.value)} aria-label="Status filter">
            <option value="all">All statuses</option>
            {(D.CHURN_STATUSES || []).map(s => <option key={s.key} value={s.key}>{s.label}</option>)}
          </select>
          <select value={reasonFilter} onChange={e => setReasonFilter(e.target.value)} aria-label="Reason filter">
            <option value="all">All reasons</option>
            {(D.CHURN_REASON_CATEGORIES || []).map(r => <option key={r.key} value={r.key}>{r.key}</option>)}
          </select>
          <select value={ownerFilter} onChange={e => setOwnerFilter(e.target.value)} aria-label="Owner filter">
            <option value="all">All owners</option>
            {reps.map(u => <option key={u.id} value={u.id}>{u.name}</option>)}
          </select>
          <select value={windowFilter} onChange={e => setWindowFilter(e.target.value)} aria-label="Renewal window filter">
            <option value="all">All renewal windows</option>
            <option value="30d">Next 30 days</option>
            <option value="60d">Next 60 days</option>
            <option value="90d">Next 90 days</option>
          </select>
          <button className="btn btn-sm" onClick={exportCsv} title="Download CSV of current filtered view">⤓ CSV</button>
        </FilterToolbar>
        <table className="tbl tbl-flush">
          <thead>
            <tr>
              {sortHeader("Account", "accountName")}
              {sortHeader("Status", "status")}
              {sortHeader("Tier", "tier")}
              {sortHeader("ARR", "arrPence", true)}
              {sortHeader("Renewal", "renewalDate")}
              {sortHeader("Days", "daysToRenewal", true)}
              {sortHeader("Tenure", "tenureMonths", true)}
              <th>Owner</th>
              <th>Reason</th>
              <th>Champion</th>
              <th>Logins (30d)</th>
              <th>Seats</th>
              <th>Health</th>
              <th>Save</th>
            </tr>
          </thead>
          <tbody>
            {rows.map(a => (
              <ChurnDetailsRow key={a.accountId} a={a} onOpen={() => setOpenId(a.accountId)} />
            ))}
            {!rows.length && (
              <tr><td colSpan="14" className="muted" style={{ padding: 16, textAlign: "center" }}>No accounts match the current filters.</td></tr>
            )}
          </tbody>
        </table>
      </div>
      {open && (
        <ChurnDrawer
          account={open}
          renewalEvents={renewalEvents.filter(e => e.accountId === open.accountId)}
          onClose={() => setOpenId(null)}
          onEdit={() => setEditId(open.accountId)}
        />
      )}
      {editId && (
        <EditChurnDialog
          account={accounts.find(a => a.accountId === editId)}
          onClose={() => setEditId(null)}
        />
      )}
    </>
  );
}

function describeQuickFilter(q) {
  if (q === "renewal-pool") return "renewing in next 90 days";
  if (q === "at-risk")      return "at-risk in next 90 days";
  if (q === "churned")      return "churned in period";
  if (q && q.month)         return "renewing in " + formatMonthLabel(q.month);
  return String(q);
}

// ── Details row ─────────────────────────────────────────────────────
function ChurnDetailsRow({ a, onOpen }) {
  const tpC = window.tpChurn;
  const status = (D.CHURN_STATUSES || []).find(s => s.key === a.status) || { tone: "slate", label: a.status };
  const reason = (D.CHURN_REASON_CATEGORIES || []).find(r => r.key === a.reasonCategory);
  const seatsText = a.latest
    ? `${a.latest.activeSeats || 0}/${a.latest.licensedSeats || 0}`
    : "—";
  const seatRatio = a.latest && a.latest.licensedSeats > 0
    ? (a.latest.activeSeats || 0) / a.latest.licensedSeats : null;
  const logins = (a.snapshots || []).map(s => s.logins30d || 0);
  const champion = a.championContactId ? getContact(a.championContactId) : null;
  const dayCell = a.daysToRenewal == null ? "—"
    : a.daysToRenewal < 0 ? <span className="muted">{a.daysToRenewal}d</span>
    : a.daysToRenewal <= 30 ? <span style={{ color: "var(--red)" }}>{a.daysToRenewal}d</span>
    : a.daysToRenewal <= 90 ? <span style={{ color: "var(--amber)" }}>{a.daysToRenewal}d</span>
    : a.daysToRenewal + "d";
  return (
    <tr onClick={onOpen} style={{ cursor: "pointer" }}>
      <td>
        <span className="tbl-link">{a.accountName}</span>
        {a.industry && <span className="muted" style={{ fontSize: 11, marginLeft: 6 }}>· {a.industry}</span>}
      </td>
      <td>
        <Badge tone={status.tone}>{status.label}</Badge>
        {a.flagSuggested && (
          <Badge tone="amber" dot>Flag suggested</Badge>
        )}
      </td>
      <td>{a.tier || "—"}</td>
      <td className="right numeric">{tpC.formatPounds(a.arrPence)}</td>
      <td>{tpC.formatDateUK(a.renewalDate)}</td>
      <td className="right numeric">{dayCell}</td>
      <td className="right numeric">{a.tenureMonths != null ? a.tenureMonths + "m" : "—"}</td>
      <td>{a.renewalOwnerId ? <Avatar user={a.renewalOwnerId} size={22} /> : <span className="muted">—</span>}</td>
      <td>{reason ? <Badge tone={reason.tone}>{reason.key}</Badge> : <span className="muted">—</span>}</td>
      <td>
        {champion
          ? <>{champion.first} {champion.last}{a.championLeft ? <Badge tone="red" dot>left</Badge> : null}</>
          : <span className="muted">—</span>}
      </td>
      <td>
        {logins.length
          ? <><MiniSparkline values={logins} tone={a.status === "churned" ? "var(--red)" : a.status === "at_risk" ? "var(--amber)" : "var(--green)"} /> <span className="muted" style={{ fontSize: 11, marginLeft: 4 }}>{a.latest && a.latest.logins30d != null ? a.latest.logins30d : "—"}</span></>
          : <span className="muted">—</span>}
      </td>
      <td>
        {seatRatio != null ? (
          <span style={{ color: seatRatio < 0.4 ? "var(--red)" : seatRatio < 0.7 ? "var(--amber)" : "var(--text)" }}>
            {seatsText}
          </span>
        ) : <span className="muted">{seatsText}</span>}
      </td>
      <td>
        {a.latest && a.latest.healthScore != null
          ? <HealthDotForScore score={a.latest.healthScore} />
          : <span className="muted">—</span>}
      </td>
      <td>
        {a.saveAttempted
          ? <Badge tone={a.saveOutcome === "Lost" ? "red" : a.saveOutcome === "Saved" ? "green" : "amber"}>
              {a.saveOutcome || "In flight"}
            </Badge>
          : <span className="muted">—</span>}
      </td>
    </tr>
  );
}

// Lightweight health-score chip independent of the customer-health
// config in ui.jsx — uses fixed Churn thresholds (50/75) which match the
// auto-flag rule (< 50 trips the rule).
function HealthDotForScore({ score }) {
  const tone = score >= 75 ? "green" : score >= 50 ? "amber" : "red";
  return <Badge tone={tone} dot>{score}</Badge>;
}

// ── Drawer (full record + monthly chart + at-risk rules) ────────────
function ChurnDrawer({ account, renewalEvents, onClose, onEdit }) {
  const tpC = window.tpChurn;
  const a = account;
  const champion = a.championContactId ? getContact(a.championContactId) : null;
  const owner = a.renewalOwnerId ? getUser(a.renewalOwnerId) : null;
  const reason = (D.CHURN_REASON_CATEGORIES || []).find(r => r.key === a.reasonCategory);
  const tripped = a.ruleEval.ruleResults;

  // ESC closes (matches Dialog convention from ui.jsx).
  useEffect(() => {
    const onKey = e => { if (e.key === "Escape") onClose(); };
    document.addEventListener("keydown", onKey);
    return () => document.removeEventListener("keydown", onKey);
  }, [onClose]);

  return (
    <div
      className="dialog-backdrop"
      onClick={e => { if (e.target === e.currentTarget) onClose(); }}
      role="dialog"
      aria-label={`${a.accountName} churn detail`}
    >
      <div style={{
        position: "fixed", top: 0, right: 0, bottom: 0,
        width: "min(640px, 92vw)", background: "var(--bg)", color: "var(--text)",
        boxShadow: "-8px 0 24px rgba(0,0,0,.18)", overflowY: "auto",
      }}>
        <div style={{ padding: "16px 20px", borderBottom: "1px solid var(--border)", display: "flex", justifyContent: "space-between", alignItems: "flex-start" }}>
          <div>
            <h2 style={{ margin: 0, fontSize: 18 }}>{a.accountName}</h2>
            <div style={{ marginTop: 4, fontSize: 12, color: "var(--text-muted)" }}>
              {a.industry || ""}{a.region ? ` · ${a.region}` : ""} · {a.tier || "—"}
            </div>
          </div>
          <div style={{ display: "flex", gap: 6 }}>
            <button className="btn btn-sm" onClick={onEdit}>Edit</button>
            <button className="btn btn-sm" onClick={onClose}>Close</button>
          </div>
        </div>

        <div style={{ padding: "16px 20px" }}>
          <div className="kv">
            <div className="kv-label">Status</div>
            <div className="kv-value">
              <Badge tone={(D.CHURN_STATUSES.find(s => s.key === a.status) || { tone: "slate" }).tone}>
                {(D.CHURN_STATUSES.find(s => s.key === a.status) || { label: a.status }).label}
              </Badge>
              {a.flagSuggested && <Badge tone="amber" dot>Flag suggested</Badge>}
            </div>
            <div className="kv-label">ARR</div>
            <div className="kv-value">{tpC.formatPounds(a.arrPence)} <span className="muted" style={{ fontSize: 11 }}>· annual</span></div>
            <div className="kv-label">Contract</div>
            <div className="kv-value">
              {tpC.formatDateUK(a.contractStart)} → {tpC.formatDateUK(a.contractEnd)}
              {a.tenureMonths != null && <span className="muted" style={{ marginLeft: 8, fontSize: 11 }}>{a.tenureMonths} months tenure</span>}
            </div>
            <div className="kv-label">Renewal</div>
            <div className="kv-value">
              {tpC.formatDateUK(a.renewalDate)}
              {a.daysToRenewal != null && (
                <span className="muted" style={{ marginLeft: 8, fontSize: 11 }}>
                  {a.daysToRenewal >= 0 ? `${a.daysToRenewal} days away` : `${-a.daysToRenewal} days ago`}
                </span>
              )}
            </div>
            <div className="kv-label">Renewal owner</div>
            <div className="kv-value">{owner ? owner.name : <span className="muted">—</span>}</div>
            <div className="kv-label">Champion</div>
            <div className="kv-value">
              {champion ? <>{champion.first} {champion.last}{a.championLeft ? <Badge tone="red" dot>left</Badge> : ""}</> : <span className="muted">—</span>}
            </div>
            <div className="kv-label">Reason</div>
            <div className="kv-value">
              {reason ? <Badge tone={reason.tone}>{reason.key}</Badge> : <span className="muted">—</span>}
            </div>
            {a.reasonNarrative && (
              <>
                <div className="kv-label">Narrative</div>
                <div className="kv-value" style={{ fontSize: 13 }}>{a.reasonNarrative}</div>
              </>
            )}
            <div className="kv-label">Save</div>
            <div className="kv-value">
              {a.saveAttempted
                ? <>Attempted{a.saveOutcome ? ` · ${a.saveOutcome}` : ""}{a.recoverable ? <> · <Badge tone="amber">Recoverable</Badge></> : ""}</>
                : <span className="muted">Not attempted</span>}
            </div>
            {a.winBackNote && (
              <>
                <div className="kv-label">Win-back</div>
                <div className="kv-value" style={{ fontSize: 13 }}>{a.winBackNote}</div>
              </>
            )}
          </div>
        </div>

        {/* At-risk auto-flag rule trace */}
        <div style={{ padding: "0 20px 16px" }}>
          <h3 style={{ fontSize: 13, color: "var(--text-muted)", margin: "8px 0 6px", textTransform: "uppercase", letterSpacing: 0.5 }}>
            At-risk rules{" "}
            {a.ruleEval.suggested ? <Badge tone="amber">{a.ruleEval.tripCount}/4 tripped — suggested</Badge>
              : a.ruleEval.tripCount > 0 ? <Badge tone="slate">{a.ruleEval.tripCount}/4 tripped</Badge>
              : <Badge tone="slate">0/4 tripped</Badge>}
          </h3>
          <table className="tbl tbl-flush" style={{ fontSize: 12 }}>
            <tbody>
              {tripped.map(r => (
                <tr key={r.key}>
                  <td style={{ width: 130 }}>{r.label}</td>
                  <td>
                    {r.tripped ? <Badge tone="red" dot>tripped</Badge> : <Badge tone="green" dot>ok</Badge>}
                  </td>
                  <td className="muted">{r.detail}</td>
                </tr>
              ))}
            </tbody>
          </table>
          {a.flagSuggested && !a.atRiskConfirmed && (
            <div style={{ marginTop: 8, display: "flex", gap: 6 }}>
              <button
                className="btn btn-primary btn-sm"
                onClick={async () => {
                  await tpAPI.churnQualitative.save(a.accountId, {
                    status: "at_risk",
                    atRiskConfirmed: true,
                    atRiskConfirmedAt: new Date().toISOString(),
                  });
                  onClose();
                }}
              >Confirm at-risk</button>
              <button
                className="btn btn-sm"
                onClick={async () => {
                  // Reject by recording a confirmation against current
                  // healthy state — keeps the badge from re-suggesting.
                  await tpAPI.churnQualitative.save(a.accountId, {
                    atRiskConfirmed: true,
                  });
                  onClose();
                }}
              >Dismiss suggestion</button>
            </div>
          )}
        </div>

        {/* Monthly usage history chart */}
        <div style={{ padding: "0 20px 16px" }}>
          <h3 style={{ fontSize: 13, color: "var(--text-muted)", margin: "8px 0 6px", textTransform: "uppercase", letterSpacing: 0.5 }}>Monthly usage</h3>
          {a.snapshots.length ? (
            <UsageHistoryChart snapshots={a.snapshots} />
          ) : <div className="muted">No usage snapshots recorded.</div>}
        </div>

        {/* Renewal history */}
        <div style={{ padding: "0 20px 20px" }}>
          <h3 style={{ fontSize: 13, color: "var(--text-muted)", margin: "8px 0 6px", textTransform: "uppercase", letterSpacing: 0.5 }}>Renewal history</h3>
          {renewalEvents.length ? (
            <table className="tbl tbl-flush" style={{ fontSize: 12 }}>
              <thead>
                <tr><th>Date</th><th>Outcome</th><th className="right">From</th><th className="right">To</th><th className="right">Change</th><th>Reason</th></tr>
              </thead>
              <tbody>
                {renewalEvents.slice().sort((x, y) => x.renewalDate.localeCompare(y.renewalDate)).map(r => (
                  <tr key={r.id}>
                    <td>{tpC.formatDateUK(r.renewalDate)}</td>
                    <td><Badge tone={r.outcome === "churned" ? "red" : r.outcome === "downgraded" ? "amber" : r.outcome === "upgraded" ? "green" : "slate"}>{r.outcome}</Badge></td>
                    <td className="right numeric">{tpC.formatPounds(r.startArrPence)}</td>
                    <td className="right numeric">{tpC.formatPounds(r.endArrPence)}</td>
                    <td className="right numeric" style={{ color: r.arrChangePence < 0 ? "var(--red)" : r.arrChangePence > 0 ? "var(--green)" : "var(--text-muted)" }}>
                      {r.arrChangePence > 0 ? "+" : ""}{tpC.formatPounds(r.arrChangePence)}
                    </td>
                    <td className="muted">{r.reasonCategory || "—"}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          ) : <div className="muted">No prior renewals recorded yet.</div>}
        </div>
      </div>
    </div>
  );
}

function UsageHistoryChart({ snapshots }) {
  const W = 560, H = 140, padL = 30, padR = 8, padT = 10, padB = 24;
  const innerW = W - padL - padR;
  const innerH = H - padT - padB;
  const stepX = innerW / Math.max(snapshots.length - 1, 1);
  const loginsMax = Math.max(1, ...snapshots.map(s => s.logins30d || 0));
  const healthMax = 100;
  function yLogins(v) { return padT + innerH - (innerH * (v / loginsMax)); }
  function yHealth(v) { return padT + innerH - (innerH * (v / healthMax)); }
  let pLogins = "", pHealth = "";
  snapshots.forEach((s, i) => {
    const x = padL + i * stepX;
    pLogins += (i === 0 ? "M " : " L ") + x.toFixed(1) + " " + yLogins(s.logins30d || 0).toFixed(1);
    pHealth += (i === 0 ? "M " : " L ") + x.toFixed(1) + " " + yHealth(s.healthScore || 0).toFixed(1);
  });
  return (
    <svg viewBox={`0 0 ${W} ${H}`} width="100%" role="img" aria-label="Monthly usage trend">
      <line x1={padL} x2={padL + innerW} y1={padT + innerH} y2={padT + innerH} stroke="var(--border)" />
      <path d={pLogins} stroke="var(--accent)" fill="none" strokeWidth="1.5" />
      <path d={pHealth} stroke="var(--blue, #3b82f6)" fill="none" strokeWidth="1.5" strokeDasharray="3 3" />
      {snapshots.map((s, i) => {
        const x = padL + i * stepX;
        return (
          <g key={s.snapshotMonth}>
            <circle cx={x} cy={yLogins(s.logins30d || 0)} r="2.5" fill="var(--accent)">
              <title>{formatMonthLabel(s.snapshotMonth)}: {s.logins30d || 0} logins · health {s.healthScore || 0}</title>
            </circle>
            {(i % 2 === 0) && (
              <text x={x} y={H - padB + 12} fontSize="9" fill="var(--text-muted)" textAnchor="middle">
                {formatMonthLabel(s.snapshotMonth)}
              </text>
            )}
          </g>
        );
      })}
      <g transform={`translate(${padL + 4}, ${padT - 2})`}>
        <rect x="0" y="-8" width="10" height="2" fill="var(--accent)" />
        <text x="14" y="-3" fontSize="10" fill="var(--text)">logins (30d)</text>
        <rect x="80" y="-8" width="10" height="2" fill="var(--blue, #3b82f6)" />
        <text x="94" y="-3" fontSize="10" fill="var(--text)">health</text>
      </g>
    </svg>
  );
}

// ── Inline edit dialog ───────────────────────────────────────────────
function EditChurnDialog({ account, onClose }) {
  const [form, setForm] = useState({
    status: account.status || "active",
    arrPence: account.arrPence == null ? "" : String(account.arrPence / 100),
    tier: account.tier || "",
    renewalDate: account.renewalDate || "",
    renewalOwnerId: account.renewalOwnerId || "",
    championContactId: account.championContactId || "",
    championLeft: !!account.championLeft,
    reasonCategory: account.reasonCategory || "",
    reasonNarrative: account.reasonNarrative || "",
    saveAttempted: !!account.saveAttempted,
    saveOutcome: account.saveOutcome || "",
    recoverable: !!account.recoverable,
    winBackNote: account.winBackNote || "",
  });
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState(null);
  function set(k, v) { setForm(f => ({ ...f, [k]: v })); }
  async function handleSave() {
    setSaving(true); setError(null);
    try {
      const arr = form.arrPence === "" ? null : Math.round(Number(form.arrPence) * 100);
      if (arr != null && (!Number.isFinite(arr) || arr < 0)) {
        throw new Error("ARR must be a non-negative number of pounds.");
      }
      await tpAPI.churnQualitative.save(account.accountId, {
        status: form.status,
        tier: form.tier || null,
        arrPence: arr,
        renewalDate: form.renewalDate || null,
        renewalOwnerId: form.renewalOwnerId || null,
        championContactId: form.championContactId || null,
        championLeft: form.championLeft,
        reasonCategory: form.reasonCategory || null,
        reasonNarrative: form.reasonNarrative.trim() || null,
        saveAttempted: form.saveAttempted,
        saveOutcome: form.saveOutcome || null,
        recoverable: form.recoverable,
        winBackNote: form.winBackNote.trim() || null,
      });
      onClose();
    } catch (e) {
      setError(e.message || "Failed to save.");
      setSaving(false);
    }
  }
  const reps = assignableReps(["CSM", "AE"], form.renewalOwnerId);
  const accountContacts = (D.CONTACTS || []).filter(c => c.accountId === account.accountId);
  return (
    <Dialog
      title={"Edit churn record · " + account.accountName}
      onClose={onClose}
      footer={<>
        <button className="btn btn-sm" onClick={onClose}>Cancel</button>
        <button className="btn btn-primary btn-sm" onClick={handleSave} disabled={saving}>
          {saving ? "Saving…" : "Save"}
        </button>
      </>}
    >
      <div className="form-row">
        <div className="form-field">
          <label>Status</label>
          <select value={form.status} onChange={e => set("status", e.target.value)}>
            {(D.CHURN_STATUSES || []).map(s => <option key={s.key} value={s.key}>{s.label}</option>)}
          </select>
        </div>
        <div className="form-field">
          <label>Tier</label>
          <select value={form.tier} onChange={e => set("tier", e.target.value)}>
            <option value="">—</option>
            <option>Tier 1</option><option>Tier 2</option><option>Tier 3</option>
          </select>
        </div>
      </div>
      <div className="form-row">
        <div className="form-field">
          <label>ARR (£)</label>
          <input type="number" min="0" step="1" value={form.arrPence} onChange={e => set("arrPence", e.target.value)} placeholder="e.g. 120000" />
        </div>
        <div className="form-field">
          <label>Renewal date</label>
          <input type="date" value={form.renewalDate} onChange={e => set("renewalDate", e.target.value)} />
        </div>
      </div>
      <div className="form-row">
        <div className="form-field">
          <label>Renewal owner</label>
          <select value={form.renewalOwnerId} onChange={e => set("renewalOwnerId", e.target.value)}>
            <option value="">— Unassigned —</option>
            {reps.map(u => <option key={u.id} value={u.id}>{u.name} ({u.role})</option>)}
          </select>
        </div>
        <div className="form-field">
          <label>Champion</label>
          <select value={form.championContactId} onChange={e => set("championContactId", e.target.value)}>
            <option value="">— None —</option>
            {accountContacts.map(c => <option key={c.id} value={c.id}>{c.first} {c.last} · {c.title}</option>)}
          </select>
        </div>
      </div>
      <div className="form-row">
        <div className="form-field">
          <label>Champion left?</label>
          <select value={form.championLeft ? "yes" : "no"} onChange={e => set("championLeft", e.target.value === "yes")}>
            <option value="no">No</option><option value="yes">Yes</option>
          </select>
        </div>
        <div className="form-field">
          <label>Reason category</label>
          <select value={form.reasonCategory} onChange={e => set("reasonCategory", e.target.value)}>
            <option value="">— None —</option>
            {(D.CHURN_REASON_CATEGORIES || []).map(r => <option key={r.key} value={r.key}>{r.key}</option>)}
          </select>
        </div>
      </div>
      <div className="form-field">
        <label>Reason narrative</label>
        <textarea value={form.reasonNarrative} onChange={e => set("reasonNarrative", e.target.value)} placeholder="What we heard, what we tried, what's still open." rows="3" />
      </div>
      <div className="form-row">
        <div className="form-field">
          <label>Save attempted?</label>
          <select value={form.saveAttempted ? "yes" : "no"} onChange={e => set("saveAttempted", e.target.value === "yes")}>
            <option value="no">No</option><option value="yes">Yes</option>
          </select>
        </div>
        <div className="form-field">
          <label>Save outcome</label>
          <select value={form.saveOutcome} onChange={e => set("saveOutcome", e.target.value)}>
            <option value="">—</option>
            <option>In flight</option><option>Saved</option><option>Lost</option><option>Pending exec meeting</option>
          </select>
        </div>
      </div>
      <div className="form-row">
        <div className="form-field">
          <label>Recoverable?</label>
          <select value={form.recoverable ? "yes" : "no"} onChange={e => set("recoverable", e.target.value === "yes")}>
            <option value="no">No</option><option value="yes">Yes</option>
          </select>
        </div>
        <div className="form-field">
          <label>Win-back note</label>
          <input value={form.winBackNote} onChange={e => set("winBackNote", e.target.value)} placeholder="e.g. New CFO in Q3 — revisit Sep" />
        </div>
      </div>
      {error && <div className="form-error">{error}</div>}
    </Dialog>
  );
}

window.ChurnPage = ChurnPage;
